import React from 'react';
import * as R from 'ramda';
import {isNotNil} from '@practicegenius/pg-util/src/TypescriptUtils';
import {pipe} from '@practicegenius/pg-util/src/FunctionUtils';
import {UseMachineType} from '../machines/useAccountRegistrationMachineType';
import lang from '../lang';
import {
  calculateProgress,
  primaryActionButtonProps,
  PrimaryActionButtonProps,
  SecondaryActionButtonProps,
  secondaryActionButtonProps,
} from './utils';
import {AccountRelationship, RegistrationReason} from '../data/types';
import {Event, State} from '../machines/machineTypes';

type Props = {
  registrationMachine: UseMachineType;
};

type ScreenProps = Props & {
  lang: typeof lang.tellUsMore;
  onFormSubmit: React.ComponentProps<'form'>['onSubmit'];
  progress: number;
  actionButtons: {
    primary: PrimaryActionButtonProps;
    secondary: SecondaryActionButtonProps;
  };
  relationshipInputProps: {
    valueLabelMap: [AccountRelationship, string][];
    value: AccountRelationship | null;
    onChange: (value: AccountRelationship) => unknown;
    onBlur: () => unknown;
    error?: string;
  };
  whyInputProps: {
    valueLabelMap: [RegistrationReason, string][];
    value: RegistrationReason | null;
    onChange: (value: RegistrationReason) => unknown;
    onBlur: () => unknown;
    error?: string;
  };
  commentsInputProps: {
    value: string | null;
    onChange: (value: string) => unknown;
    onBlur: () => unknown;
    error?: string;
  };
};

export type TellUsMoreScreenType = React.ComponentType<ScreenProps>;

const screenLang = lang.tellUsMore;
const tellUsMoreProps = ['relationship', 'why', 'questionsOrComments'];
const anyPropsAre = (pred: (x: any) => boolean) =>
  pipe(R.props(['relationship', 'why']), R.any(pred)) as (
    item: object | undefined
  ) => boolean;
const anyRequiredPropsAreNil = anyPropsAre(R.isNil);
const anyRequiredPropsAreNonNil = anyPropsAre(isNotNil);

const fieldValueLabelMap = {
  relationship: [
    [
      AccountRelationship.friend,
      screenLang.inputs.relationship.valueLabels.friend,
    ],
    [
      AccountRelationship.parentOrGuardian,
      screenLang.inputs.relationship.valueLabels.parentOrGuardian,
    ],
    [
      AccountRelationship.member,
      screenLang.inputs.relationship.valueLabels.member,
    ],
    [
      AccountRelationship.other,
      screenLang.inputs.relationship.valueLabels.other,
    ],
  ] as [AccountRelationship, string][],
  why: [
    [
      RegistrationReason.productsOrServices,
      screenLang.inputs.why.valueLabels.products,
    ],
    [RegistrationReason.joinFamily, screenLang.inputs.why.valueLabels.family],
    [
      RegistrationReason.accessAccount,
      screenLang.inputs.why.valueLabels.account,
    ],
    [RegistrationReason.other, screenLang.inputs.why.valueLabels.other],
  ] as [RegistrationReason, string][],
};

export const TellUsMore: React.FC<Props & {children: TellUsMoreScreenType}> = ({
  registrationMachine,
  children: Screen,
}) => {
  const [{context, matches}, send] = registrationMachine;
  const progress = calculateProgress({
    draftAccount: context.account,
    location: context.navigator.location,
  });
  const onFormSubmit: ScreenProps['onFormSubmit'] = e => {
    e.preventDefault();
    send({
      type: Event.UPDATE_REGISTRATION,
      payload: R.pick(
        // @ts-expect-error
        tellUsMoreProps,
        context.account
      ),
    });
  };
  const actionButtons = {
    primary: primaryActionButtonProps({
      children: lang.tellUsMore.buttons.primary.title,
      loading: matches({
        [State.tellUsMore]: State.submittingLeadSourceInfo,
      }),
      disabled:
        anyRequiredPropsAreNil(context.account) ||
        anyRequiredPropsAreNonNil(context.validationErrors?.account),
    }),
    secondary: secondaryActionButtonProps({
      children: lang.tellUsMore.buttons.secondary.title,
      onClick: () => context.navigator.goBack(),
      loading: false,
      disabled: false,
    }),
  };

  function makeFieldProps<Val = string>(
    field: 'relationship' | 'why' | 'questionsOrComments'
  ) {
    const ctxVal = context.account[field];
    return {
      value: ctxVal as Val | null,
      error: context.validationErrors?.account?.[field],
      onChange: (value: Val) => {
        send({
          type: Event.UPDATE_DATA,
          payload: {
            account: {
              [field]: value,
            },
          },
        });
      },
      onBlur: () => {
        if (field !== 'questionsOrComments' && !ctxVal) {
          send({
            type: Event.HAS_VALIDATION_ERRORS,
            errors: {
              account: {
                [field]: screenLang.inputs[field].errors.blank,
              },
            },
          });
        }
      },
    };
  }

  const renderProps: ScreenProps = {
    registrationMachine,
    lang: lang.tellUsMore,
    onFormSubmit,
    progress,
    actionButtons,
    relationshipInputProps: {
      ...makeFieldProps<AccountRelationship>('relationship'),
      valueLabelMap: fieldValueLabelMap.relationship,
    },
    whyInputProps: {
      ...makeFieldProps<RegistrationReason>('why'),
      valueLabelMap: fieldValueLabelMap.why,
    },
    commentsInputProps: {
      ...makeFieldProps('questionsOrComments'),
    },
  };

  return <Screen {...renderProps} />;
};
